import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTrip } from "@/hooks/useTrip";
import { useShareTrip } from "@/hooks/useShareTrip";
import { PageShell } from "@/components/PageShell";
import { BrandHeader } from "@/components/BrandHeader";
import { Button } from "@/components/Button";
import "./save-link.css";

const COPIED_LABEL_MS = 1600;

/**
 * <SaveLinkStep> — the one-time, creator-only interstitial shown right after
 * «Створити», before entering the trip (AC-04.6/.7, wireframes/save-link.md).
 *
 * Standalone route `/t/:slug/created` — NOT nested under <TripLoader>, so it
 * has no tab bar; it is a "before you enter the trip" step. It only ever shows
 * the link (built from the slug in the URL), so a cold load / refresh is
 * harmless. Friends who open the shared link never reach it.
 *
 * No localStorage, no "secured"/dismiss state — nothing is persisted. The
 * durable re-copy path is always the header ⤴ (the screen hands off to it).
 */
export function SaveLinkStep() {
  const { slug = "" } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  // Used only to personalise the copy — never blocks the screen (the link
  // needs only the slug, which is already in the URL).
  const tripQ = useTrip(slug);
  const tripName = tripQ.data?.name;

  // useShareTrip keys off trip.trip_id; the slug IS the trip id here.
  const copyLink = useShareTrip({ trip_id: slug });

  const [copied, setCopied] = useState(false);
  const [manualHint, setManualHint] = useState(false);
  const [pulse, setPulse] = useState(false);
  const copiedTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    return () => {
      if (copiedTimer.current !== undefined) window.clearTimeout(copiedTimer.current);
    };
  }, []);

  const url = `${window.location.origin}/t/${slug}`;
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const canCopy =
    typeof navigator !== "undefined" && typeof navigator.clipboard?.writeText === "function";

  async function handleCopy() {
    const result = await copyLink();
    if (result === "copied") {
      setManualHint(false);
      setCopied(true);
      if (copiedTimer.current !== undefined) window.clearTimeout(copiedTimer.current);
      copiedTimer.current = window.setTimeout(() => setCopied(false), COPIED_LABEL_MS);
      return;
    }
    // Graceful failure (AC-04.8): info-tone hint + a single URL-row pulse.
    setManualHint(true);
    setPulse(false);
    // Restart the animation on a fresh tap.
    requestAnimationFrame(() => setPulse(true));
  }

  function handleShare() {
    // Native-share success or cancel (AbortError) both leave the screen as-is.
    void navigator.share?.({ url }).catch(() => undefined);
  }

  return (
    <PageShell header={<BrandHeader />}>
      <section
        className="wp-savelink"
        aria-labelledby="savelink-title"
        data-testid="save-link-step"
      >
        <span className="wp-savelink__glyph" aria-hidden="true">
          🎉
        </span>
        <h1 id="savelink-title" className="wp-savelink__title">
          Поїздку створено
        </h1>

        <div className="wp-savelink__panel">
          <p className="wp-savelink__lead">
            <span aria-hidden="true">🔖 </span>
            Це єдине посилання на «{tripName ?? "цю поїздку"}».{" "}
            <span className="wp-savelink__lead-muted">
              Акаунтів немає — хто має лінку, той у поїздці. Збережи його зараз, щоб
              не загубити поїздку.
            </span>
          </p>

          <div
            className={`wp-savelink__url${pulse ? " wp-savelink__url--pulse" : ""}`}
            data-testid="save-link-url"
            onAnimationEnd={() => setPulse(false)}
          >
            <span className="wp-savelink__url-text">{url}</span>
            <span className="wp-savelink__url-glyph" aria-hidden="true">
              ⧉
            </span>
          </div>
        </div>

        {manualHint ? (
          <p className="wp-savelink__hint" role="status" data-testid="save-link-manual-hint">
            Не вдалось скопіювати — виділи й скопіюй посилання вище ↑
          </p>
        ) : null}

        <div className="wp-savelink__actions">
          {canCopy ? (
            <Button
              size="lg"
              fullWidth
              onClick={handleCopy}
              iconLeft={<span aria-hidden="true">{copied ? "✓" : "⧉"}</span>}
              data-testid="save-link-copy"
            >
              {copied ? "Скопійовано" : "Скопіювати посилання"}
            </Button>
          ) : null}
          {canShare ? (
            <Button
              size="lg"
              fullWidth
              variant="secondary"
              onClick={handleShare}
              iconLeft={<span aria-hidden="true">⤴</span>}
              data-testid="save-link-share"
            >
              Поділитись
            </Button>
          ) : null}
        </div>

        <p className="wp-savelink__handoff">
          Згодом скопіюєш будь-коли — кнопкою ⤴ вгорі.
        </p>

        <button
          type="button"
          className="wp-savelink__proceed"
          onClick={() => navigate(`/t/${slug}/receipts`)}
          data-testid="save-link-proceed"
        >
          До чеків →
        </button>
      </section>
    </PageShell>
  );
}
